import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Shield, FileSpreadsheet, FileText, Filter, Inbox } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TIPO_LABELS = {
  accesso: 'Accesso (Art. 15)',
  rettifica: 'Rettifica (Art. 16)',
  cancellazione: 'Cancellazione (Art. 17)',
  limitazione: 'Limitazione (Art. 18)',
  portabilita: 'Portabilità (Art. 20)',
  opposizione: 'Opposizione (Art. 21)',
  altro: 'Altro',
};
const ESITO_BADGES = {
  evasa: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  in_lavorazione: 'bg-amber-100 text-amber-800 border-amber-300',
  rifiutata: 'bg-red-100 text-red-800 border-red-300',
};
const ESITO_LABELS = {
  evasa: 'Evasa',
  in_lavorazione: 'In lavorazione',
  rifiutata: 'Rifiutata',
};

const RegistroGdprPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAnno, setFilterAnno] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterEsito, setFilterEsito] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterAnno) params.anno = filterAnno;
      if (filterTipo) params.tipo = filterTipo;
      if (filterEsito) params.esito = filterEsito;
      const { data } = await axios.get(`${API_URL}/admin/gdpr-requests-log`, { params });
      setLogs(data.logs || []);
    } catch (e) {
      toast.error('Errore caricamento registro');
    } finally {
      setLoading(false);
    }
  }, [filterAnno, filterTipo, filterEsito]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleExport = async (format) => {
    try {
      const params = {};
      if (filterAnno) params.anno = filterAnno;
      if (filterTipo) params.tipo = filterTipo;
      if (filterEsito) params.esito = filterEsito;
      const res = await axios.get(`${API_URL}/admin/gdpr-requests-log/export/${format}`, {
        params, responseType: 'blob',
      });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Registro_GDPR_${filterAnno || 'completo'}_${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} completato`);
    } catch {
      toast.error('Errore export');
    }
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString('it-IT', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  const anniDisponibili = Array.from(
    new Set(logs.map((l) => (l.data_gestione || '').slice(0, 4)).filter(Boolean))
  ).sort().reverse();
  const currentYear = new Date().getFullYear();
  const yearsToShow = anniDisponibili.length
    ? anniDisponibili
    : [String(currentYear), String(currentYear - 1)];

  return (
    <div className="p-6 space-y-6" data-testid="registro-gdpr-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="w-7 h-7 text-purple-600" />
            Registro Accountability GDPR
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Storico ufficiale delle richieste GDPR ricevute e gestite. Documento pronto in caso di
            ispezione del Garante per la Protezione dei Dati Personali.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => handleExport('xlsx')}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="export-gdpr-xlsx-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          <Button
            onClick={() => handleExport('pdf')}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="export-gdpr-pdf-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* Filtri */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-500" /> Filtri
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">Anno</label>
            <select
              value={filterAnno}
              onChange={(e) => setFilterAnno(e.target.value)}
              className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
              data-testid="filter-anno"
            >
              <option value="">Tutti gli anni</option>
              {yearsToShow.map((y) => (<option key={y} value={y}>{y}</option>))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">Tipo richiesta</label>
            <select
              value={filterTipo}
              onChange={(e) => setFilterTipo(e.target.value)}
              className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
              data-testid="filter-tipo"
            >
              <option value="">Tutti i tipi</option>
              {Object.entries(TIPO_LABELS).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">Esito</label>
            <select
              value={filterEsito}
              onChange={(e) => setFilterEsito(e.target.value)}
              className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
              data-testid="filter-esito"
            >
              <option value="">Tutti gli esiti</option>
              {Object.entries(ESITO_LABELS).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
            </select>
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setFilterAnno(''); setFilterTipo(''); setFilterEsito(''); }}
              data-testid="filter-reset"
              className="w-full"
            >
              Azzera filtri
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabella */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            Richieste registrate ({logs.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-slate-500 py-8">Caricamento...</p>
          ) : logs.length === 0 ? (
            <div className="text-center py-12" data-testid="registro-empty">
              <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">
                Nessuna richiesta GDPR registrata con i filtri selezionati.
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Le richieste vengono aggiunte dalla pagina Utenti → bottone Shield GDPR.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="registro-gdpr-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Data gestione</TableHead>
                    <TableHead>Utente</TableHead>
                    <TableHead>Tipo richiesta</TableHead>
                    <TableHead>Canale</TableHead>
                    <TableHead>Esito</TableHead>
                    <TableHead>Azioni eseguite</TableHead>
                    <TableHead>Gestita da</TableHead>
                    <TableHead>Note</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} data-testid={`registro-row-${log.id}`}>
                      <TableCell className="text-xs whitespace-nowrap">{fmtDate(log.data_gestione)}</TableCell>
                      <TableCell>
                        <div className="text-sm font-medium">{log.user_nome || '—'}</div>
                        <div className="text-xs text-slate-500">{log.user_email}</div>
                      </TableCell>
                      <TableCell className="text-sm">{TIPO_LABELS[log.tipo_richiesta] || log.tipo_richiesta}</TableCell>
                      <TableCell className="text-sm capitalize">{log.canale_ricezione}</TableCell>
                      <TableCell>
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${ESITO_BADGES[log.esito] || 'bg-slate-100 text-slate-700'}`}>
                          {ESITO_LABELS[log.esito] || log.esito}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {log.azioni_eseguite && log.azioni_eseguite.length
                          ? log.azioni_eseguite.join(', ')
                          : '—'}
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{log.gestita_da_admin_email}</TableCell>
                      <TableCell className="text-xs text-slate-600 max-w-xs">
                        <div className="truncate" title={log.note}>{log.note || '—'}</div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RegistroGdprPage;
